import React, { useState, useMemo } from 'react';
import ImageCropper from './ImageCropper';
import getCroppedImg from '../services/getCroppedImg';
import { storage } from '../services/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../services/imageUtils';
import { 
  Camera, MapPin, Calendar, Mail, Grid, Settings, 
  LogOut, Bell, Shield, Image as ImgIcon, Edit2, Eye, Upload,
  Trophy, Star, Award, TrendingUp, Zap, Crown
} from 'lucide-react';
import { FamilyMember, Post } from '../types';
import NotificationPreferences from './NotificationPreferences';

interface ProfileProps {
  onNavigate: (tabId: string) => void;
  onSignOut: () => void;
  // NEW PROPS: We need real data to calculate the score
  members: Record<string, FamilyMember>;
  posts: Post[];
  currentUser: FamilyMember;
}

const Profile: React.FC<ProfileProps> = ({ onNavigate, onSignOut, members, posts, currentUser }) => {
  const [activeTab, setActiveTab] = useState('achievements');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [rawImage, setRawImage] = useState<string | null>(null);

  // Handle avatar upload
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropSave = async () => {
    if (!rawImage || !croppedAreaPixels) return;
    setUploading(true);
    try {
      const croppedBlob = await getCroppedImg(rawImage, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' });
      const compressed = await compressImage(croppedFile);
      const avatarRef = ref(storage, `avatars/${currentUser.id}_${Date.now()}`);
      await uploadBytes(avatarRef, compressed);
      const url = await getDownloadURL(avatarRef);
      setAvatarPreview(url);
      setShowCropper(false);
      setRawImage(null);
    } catch (err) {
      alert('Failed to upload avatar.');
    } finally {
      setUploading(false);
    }
  };

  // --- 1. GAMIFICATION ENGINE ---
  const gamification = useMemo(() => {
    // A. Calculate Stats
    const myPosts = posts.filter(p => p.authorId === currentUser.id).length;
    // Mocking "Added Members" logic (In real app, track who added whom. For now, random number based on ID length)
    const myContributedMembers = Math.floor(currentUser.id.length / 2); 
    
    // B. Calculate XP (Experience Points)
    // 50 XP per post, 100 XP per member added
    const xp = (myPosts * 50) + (myContributedMembers * 100);
    
    // C. Calculate Level
    // Level 1 = 0-500, Level 2 = 500-1500, etc.
    const level = Math.floor(xp / 500) + 1;
    const nextLevelXp = level * 500;
    const progressPercent = Math.min(100, Math.round((xp / nextLevelXp) * 100));

    // D. Assign Title
    let title = "Novice Seeker";
    if (level > 1) title = "Family Scout";
    if (level > 5) title = "Heritage Guardian";
    if (level > 10) title = "Grand Historian";

    return { xp, level, nextLevelXp, progressPercent, title, myPosts, myContributedMembers };
  }, [posts, currentUser]);

  // --- 2. BADGE SYSTEM ---
  const badges = [
    { id: 1, name: 'First Memory', icon: Camera, color: 'text-blue-500', bg: 'bg-blue-100', unlocked: gamification.myPosts > 0, desc: 'Shared your first photo' },
    { id: 2, name: 'Storyteller', icon: Edit2, color: 'text-purple-500', bg: 'bg-purple-100', unlocked: gamification.myPosts >= 5, desc: 'Shared 5+ memories' },
    { id: 3, name: 'Connector', icon: Zap, color: 'text-yellow-600', bg: 'bg-yellow-100', unlocked: gamification.myContributedMembers >= 5, desc: 'Connected 5 people' },
    { id: 4, name: 'Influencer', icon: Crown, color: 'text-pink-500', bg: 'bg-pink-100', unlocked: gamification.xp > 1000, desc: 'Earned 1000 XP' },
  ];

  const galleryItems = posts.filter(p => p.authorId === currentUser.id && p.imageUrl);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-32 md:pb-10 w-full overflow-x-hidden px-4">
      
      {/* 1. HERO CARD (Gamer Style) */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden relative">
        {/* Cover Photo */}
        <div className="h-56 w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 relative">
           <div className="absolute inset-0 bg-black/10"></div>
           <button className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-md text-white rounded-full hover:bg-white/30 transition-all">
             <Camera className="w-5 h-5" />
           </button>
        </div>

        <div className="px-8 pb-8">
          <div className="flex flex-col md:flex-row items-center md:items-end -mt-20 mb-6 gap-6">
            
            {/* Avatar with Level Badge & Upload */}
            <div className="relative group">
              <div className="w-40 h-40 rounded-full border-[6px] border-white bg-white shadow-2xl overflow-hidden relative z-10 flex items-center justify-center">
                <img
                  src={avatarPreview || currentUser.avatarUrl || `https://ui-avatars.com/api/?name=${currentUser.name}&background=random`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
                {/* Upload Button Overlay */}
                <label htmlFor="avatar-upload" className="absolute bottom-2 right-2 bg-white/80 rounded-full p-2 shadow cursor-pointer hover:bg-indigo-100 transition-all flex items-center justify-center">
                  <Camera className="w-6 h-6 text-indigo-600" />
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    onChange={handleAvatarChange}
                    disabled={uploading}
                  />
                </label>
                {showCropper && rawImage && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                    <div className="bg-white rounded-xl p-4 max-w-lg w-full flex flex-col items-center">
                      <ImageCropper
                        image={rawImage}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                      />
                      <div className="flex gap-4 mt-4">
                        <button onClick={() => setShowCropper(false)} className="px-4 py-2 rounded bg-gray-200">Cancel</button>
                        <button onClick={handleCropSave} className="px-4 py-2 rounded bg-indigo-600 text-white" disabled={uploading}>{uploading ? 'Saving...' : 'Save'}</button>
                      </div>
                    </div>
                  </div>
                )}
                {uploading && (
                  <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
                    <span className="text-indigo-600 font-bold">Uploading...</span>
                  </div>
                )}
              </div>
              {/* Level Pill */}
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-20 bg-gray-900 text-white px-4 py-1 rounded-full text-sm font-black border-4 border-white shadow-lg flex items-center gap-1 whitespace-nowrap">
                <Star className="w-3 h-3 text-yellow-400 fill-current" /> LVL {gamification.level}
              </div>
            </div>

            {/* User Info */}
            <div className="flex-1 text-center md:text-left mb-2">
              <h1 className="text-3xl font-black text-gray-900">{currentUser.name}</h1>
              <p className="text-indigo-600 font-bold text-sm uppercase tracking-wider flex items-center justify-center md:justify-start gap-1 mt-1">
                <Award className="w-4 h-4" /> {gamification.title}
              </p>
              <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto md:mx-0">
                "Tracing our roots, one leaf at a time. 🌳"
              </p>
              <p className="text-gray-400 text-xs mt-3 font-mono bg-gray-50 px-3 py-1 rounded inline-block">
                Family ID: <span className="text-gray-600 font-bold">{currentUser.familyId}</span>
              </p>
            </div>

            {/* Stats / Edit */}
            <div className="flex gap-3">
               <button onClick={() => onNavigate('tree')} className="px-6 py-2.5 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:bg-gray-800 transition-all flex items-center gap-2 active:scale-95">
                 <Eye className="w-4 h-4" /> Tree
               </button>
               <button className="px-6 py-2.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-xl font-bold hover:bg-gray-200 transition-all flex items-center gap-2">
                 <Settings className="w-4 h-4" />
               </button>
            </div>
          </div>

          {/* XP PROGRESS BAR */}
          <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 flex flex-col md:flex-row items-center gap-4">
             <div className="flex-1 w-full">
                <div className="flex justify-between text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
                   <span>XP Progress</span>
                   <span>{gamification.xp} / {gamification.nextLevelXp} XP</span>
                </div>
                <div className="h-4 w-full bg-gray-200 rounded-full overflow-hidden shadow-inner">
                   <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-1000 ease-out" style={{ width: `${gamification.progressPercent}%` }}></div>
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center md:text-left">
                   Only {gamification.nextLevelXp - gamification.xp} XP to reach <b>Level {gamification.level + 1}</b>!
                </p>
             </div>
             <div className="flex gap-8 px-4 border-l border-gray-200">
                <div className="text-center">
                   <div className="text-2xl font-black text-gray-900">{gamification.myPosts}</div>
                   <div className="text-[10px] font-bold text-gray-400 uppercase">Memories</div>
                </div>
                <div className="text-center">
                   <div className="text-2xl font-black text-gray-900">{badges.filter(b => b.unlocked).length}</div>
                   <div className="text-[10px] font-bold text-gray-400 uppercase">Badges</div>
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* 2. NAVIGATION TABS */}
      <div className="flex p-1 bg-white rounded-2xl shadow-sm border border-gray-200">
        {[
          { id: 'achievements', label: 'Trophy Room', icon: Trophy },
          { id: 'gallery', label: 'My Gallery', icon: Grid },
          { id: 'settings', label: 'Settings', icon: Settings },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${
              activeTab === tab.id 
                ? 'bg-indigo-600 text-white shadow-md' 
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4" /> <span className="hidden md:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* 3. TAB CONTENT */}
      
      {/* A. TROPHY ROOM */}
      {activeTab === 'achievements' && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 animate-fade-in">
           <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
             <Trophy className="w-6 h-6 text-yellow-500 fill-current" /> Achievements
           </h3>
           <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {badges.map((badge) => (
                <div key={badge.id} className={`p-4 rounded-2xl border-2 flex flex-col items-center text-center transition-all ${badge.unlocked ? 'border-indigo-100 bg-indigo-50/50' : 'border-gray-100 bg-gray-50 opacity-60 grayscale'}`}>
                   <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${badge.unlocked ? badge.bg : 'bg-gray-200'} ${badge.unlocked ? badge.color : 'text-gray-400'} shadow-sm`}>
                      <badge.icon className="w-7 h-7" />
                   </div>
                   <h4 className="font-bold text-gray-900 text-sm">{badge.name}</h4>
                   <p className="text-xs text-gray-500 mt-1">{badge.desc}</p>
                   {badge.unlocked ? (
                     <span className="mt-3 px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded-full uppercase">Unlocked</span>
                   ) : (
                     <span className="mt-3 px-2 py-1 bg-gray-200 text-gray-500 text-[10px] font-bold rounded-full uppercase">Locked</span>
                   )}
                </div>
              ))}
           </div>
        </div>
      )}

      {/* B. GALLERY */}
      {activeTab === 'gallery' && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 animate-fade-in">
           {galleryItems.length > 0 ? (
             <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
               {galleryItems.map(item => (
                 <div key={item.id} className="aspect-square rounded-xl overflow-hidden cursor-pointer group relative">
                    <img src={item.imageUrl} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                 </div>
               ))}
             </div>
           ) : (
             <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 bg-indigo-50 text-indigo-300 rounded-full flex items-center justify-center mb-6">
                   <ImgIcon className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No memories yet</h3>
                <p className="text-gray-500 max-w-xs mb-8">Upload photos to the feed to see them appear in your personal gallery.</p>
                <button onClick={() => onNavigate('feed')} className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all flex items-center gap-2">
                   <Upload className="w-5 h-5" /> Go to Feed
                </button>
             </div>
           )}
        </div>
      )}

      {/* C. SETTINGS */}
      {activeTab === 'settings' && (
        <div className="space-y-6 pb-20 animate-fade-in">
           {/* Notification Preferences */}
           <NotificationPreferences userId={currentUser.id} />

           {/* Sign Out Section */}
           <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8">
              <div className="p-4 bg-red-50 rounded-xl border border-red-100 flex flex-col md:flex-row items-center justify-between gap-4">
                 <div>
                    <h4 className="font-bold text-red-900">Sign Out</h4>
                    <p className="text-xs text-red-700">Securely log out of your account on this device.</p>
                 </div>
                 <button onClick={onSignOut} className="px-6 py-2 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-600 hover:text-white transition-all font-bold flex items-center gap-2 shadow-sm">
                    <LogOut className="w-4 h-4" /> Log Out
                 </button>
              </div>
           </div>
        </div>
      )}

    </div>
  );
};

export default Profile;